/**
 * Personalization utility functions
 * 
 * Helper functions to transform personalization data between API format and frontend format
 */

import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";
import { DEFAULT_DASHBOARD_APPEARANCE } from "@/lib/theme/dashboard-appearance";
import type { ThemeModeControl, FontSize } from "@/components/dashboard/DashboardThemeProvider";
import type {
  GetTenantPersonalizationResponse,
  UpdatePersonalizationRequest,
  AppearanceStyle as ApiAppearanceStyle,
  ColorPaletteId as ApiColorPaletteId,
  ThemeMode as ApiThemeMode,
  FontSize as ApiFontSize,
} from "@/openapi/generated";
import { AppearanceStyle } from "@/openapi/generated";

/**
 * Convert API ColorPaletteId (PascalCase) to frontend ColorPaletteId (lowercase)
 * API: "Default", "Blue", "Green", etc.
 * Frontend: "default", "blue", "green", etc.
 */
export function apiColorPaletteIdToFrontend(
  apiPalette: ApiColorPaletteId | null | undefined
): ColorPaletteId {
  if (!apiPalette) {
    return "default";
  }
  
  // Convert PascalCase to lowercase
  return apiPalette.toLowerCase() as ColorPaletteId;
}

/**
 * Convert frontend ColorPaletteId (lowercase) to API ColorPaletteId (PascalCase)
 * Frontend: "default", "blue", "green", etc.
 * API: "Default", "Blue", "Green", etc.
 */
export function frontendColorPaletteIdToApi(
  frontendPalette: ColorPaletteId
): ApiColorPaletteId {
  // Convert lowercase to PascalCase
  return (frontendPalette.charAt(0).toUpperCase() + frontendPalette.slice(1)) as ApiColorPaletteId;
}

/**
 * Convert API ThemeMode (PascalCase) to frontend ThemeModeControl (kebab-case)
 * API: "UserChoice", "LightOnly", "DarkOnly"
 * Frontend: "user-choice", "light-only", "dark-only"
 */
export function apiThemeModeToFrontend(
  apiThemeMode: ApiThemeMode | null | undefined
): ThemeModeControl {
  if (!apiThemeMode) {
    return "user-choice";
  }
  
  // Map PascalCase to kebab-case
  const mapping: Record<ApiThemeMode, ThemeModeControl> = {
    UserChoice: "user-choice",
    LightOnly: "light-only",
    DarkOnly: "dark-only",
  };
  
  return mapping[apiThemeMode] ?? "user-choice";
}

/**
 * Convert frontend ThemeModeControl (kebab-case) to API ThemeMode (PascalCase)
 * Frontend: "user-choice", "light-only", "dark-only"
 * API: "UserChoice", "LightOnly", "DarkOnly"
 */
export function frontendThemeModeControlToApi(
  frontendControl: ThemeModeControl
): ApiThemeMode {
  // Map kebab-case to PascalCase
  const mapping: Record<ThemeModeControl, ApiThemeMode> = {
    "user-choice": "UserChoice",
    "light-only": "LightOnly",
    "dark-only": "DarkOnly",
  };
  
  return mapping[frontendControl];
}

/**
 * Convert API FontSize (PascalCase) to frontend FontSize (lowercase)
 * API: "Small", "Medium", "Large"
 * Frontend: "small", "medium", "large"
 */
export function apiFontSizeToFrontend(
  apiFontSize: ApiFontSize | null | undefined
): FontSize {
  if (!apiFontSize) {
    return "medium";
  }
  
  // Convert PascalCase to lowercase
  return apiFontSize.toLowerCase() as FontSize;
}

/**
 * Convert frontend FontSize (lowercase) to API FontSize (PascalCase)
 * Frontend: "small", "medium", "large"
 * API: "Small", "Medium", "Large"
 */
export function frontendFontSizeToApi(
  frontendFontSize: FontSize
): ApiFontSize {
  // Convert lowercase to PascalCase
  return (frontendFontSize.charAt(0).toUpperCase() + frontendFontSize.slice(1)) as ApiFontSize;
}

/**
 * Convert API AppearanceStyle to frontend DashboardAppearance
 * API: "Modern", "Classic"
 * Frontend: "modern", "classic"
 */
export function apiDashboardAppearanceToFrontend(
  apiAppearance: ApiAppearanceStyle | null | undefined,
): DashboardAppearance {
  if (apiAppearance === AppearanceStyle.Classic) {
    return "classic";
  }
  return DEFAULT_DASHBOARD_APPEARANCE;
}

/**
 * Convert frontend DashboardAppearance to API AppearanceStyle
 */
export function frontendDashboardAppearanceToApi(
  appearance: DashboardAppearance,
): ApiAppearanceStyle {
  return appearance === "classic"
    ? AppearanceStyle.Classic
    : AppearanceStyle.Modern;
}

/**
 * Transform API personalization response to frontend format
 */
export function transformPersonalizationResponse(
  apiResponse: GetTenantPersonalizationResponse | null | undefined
): {
  colorPalette: ColorPaletteId;
  customColor: string | null;
  grainEnabled: boolean;
  gradientEnabled: boolean;
  themeModeControl: ThemeModeControl;
  fontSize: FontSize;
  dashboardAppearance: DashboardAppearance;
} | null {
  if (!apiResponse) {
    return null;
  }

  return {
    colorPalette: apiColorPaletteIdToFrontend(apiResponse.colorPaletteId),
    customColor: apiResponse.customColor ?? null, // Convert undefined to null
    grainEnabled: apiResponse.grainEnabled,
    gradientEnabled: apiResponse.gradientEnabled ?? false,
    themeModeControl: apiThemeModeToFrontend(apiResponse.themeModeControl),
    fontSize: apiFontSizeToFrontend(apiResponse.fontSize),
    dashboardAppearance: apiDashboardAppearanceToFrontend(
      apiResponse.dashboardAppearance,
    ),
  };
}

/**
 * Transform frontend personalization data to API request format
 */
export function transformPersonalizationRequest(
  colorPalette: ColorPaletteId,
  customColor: string | null,
  grainEnabled: boolean,
  gradientEnabled: boolean | null,
  themeModeControl: ThemeModeControl | null,
  fontSize: FontSize | null,
  dashboardAppearance: DashboardAppearance,
): UpdatePersonalizationRequest {
  return {
    colorPaletteId: frontendColorPaletteIdToApi(colorPalette),
    dashboardAppearance: frontendDashboardAppearanceToApi(dashboardAppearance),
    customColor: customColor,
    grainEnabled: grainEnabled,
    gradientEnabled: gradientEnabled,
    themeModeControl: themeModeControl ? frontendThemeModeControlToApi(themeModeControl) : null,
    fontSize: fontSize ? frontendFontSizeToApi(fontSize) : null,
  };
}

export type PersonalizationSettings = NonNullable<
  ReturnType<typeof transformPersonalizationResponse>
>;

export function buildPersonalizationSyncKey(
  data: PersonalizationSettings,
): string {
  return JSON.stringify(data);
}

